import { useState, useEffect } from "react";
import { X, Check, Loader2, CreditCard, BookOpen } from "lucide-react";
import { api, apiError, euro } from "@/lib/api";
import { toast } from "sonner";
import { TrainingsclubTermsDialog, useTrainingsclubTerms } from "./TrainingsclubTerms";

const inputCls = "w-full bg-[#111] border border-white/15 text-white px-4 py-3 text-base focus:border-[#ff0033] outline-none transition-colors placeholder:text-neutral-600";

const TrainingsclubModal = ({ lang = "de", onClose }) => {
  const t = (de, en) => (lang === "de" ? de : en);
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "" });
  const [loading, setLoading] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [infoError, setInfoError] = useState(false);
  const { terms, error: termsError, retry } = useTrainingsclubTerms();

  useEffect(() => { api.get("/trainingsclub/info").then((r) => setInfo(r.data)).catch(() => setInfoError(true)); }, []);

  const valid = info?.active && terms?.version === info?.terms_version && accepted && form.first_name.trim() && form.last_name.trim() && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim());

  const go = async (provider) => {
    if (!valid) { toast.error(t("Bitte Name und gültige E-Mail angeben.", "Please enter name and a valid email.")); return; }
    setLoading(provider);
    try {
      const payload = { ...form, email: form.email.trim().toLowerCase(), origin_url: window.location.origin, terms_accepted: accepted, terms_version: terms.version };
      const { data } = await api.post(`/trainingsclub/subscribe/${provider}`, payload);
      const url = data.checkout_url || data.approve_url;
      if (url) window.location.href = url;
      else { toast.error(t("Weiterleitung fehlgeschlagen.", "Redirect failed.")); setLoading(""); }
    } catch (e) { toast.error(apiError(e)); setLoading(""); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="trainingsclub-modal">
      <div className="bg-[#0A0A0A] border border-[#ff0033]/30 w-full max-w-lg max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#0A0A0A] z-10">
          <h2 data-testid="tc-membership-title" className="font-heading text-xl sm:text-2xl uppercase text-white min-w-0 break-words">{info?.headline || t("Trainingsclub", "Training Club")}</h2>
          <button onClick={onClose} data-testid="tc-close" className="text-neutral-400 hover:text-white"><X size={20} /></button>
        </div>

        <div className="p-5 sm:p-7">
          <div className="flex items-baseline gap-2 mb-1">
            <span className="font-heading text-4xl text-[#ff0033]" data-testid="tc-price">{info ? euro(info.monthly_price) : "—"}</span>
            <span className="text-neutral-400 text-sm" data-testid="tc-billing-period">/ {t("4 Wochen (28 Tage)", "4 weeks (28 days)")}</span>
          </div>
          <p className="text-neutral-400 text-sm mb-5" data-testid="tc-membership-subline">{info?.subline || t("Ab erfolgreicher Zahlung. Kein Kalendermonatsbeitrag.", "Starts with successful payment. Not a calendar-month fee.")}</p>
          {infoError && <p role="alert" data-testid="tc-info-error" className="text-red-400 text-sm mb-4">Die Mitgliedschaft konnte nicht geladen werden. Bitte später erneut versuchen.</p>}

          {info?.benefits?.length > 0 && (
            <ul className="space-y-2 mb-6" data-testid="tc-benefits">
              {info.benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-neutral-200">
                  <Check size={16} className="text-[#ff0033] mt-0.5 shrink-0" /> {b}
                </li>
              ))}
            </ul>
          )}

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="tc-first-name" placeholder={t("Vorname", "First name")} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={inputCls} />
              <input data-testid="tc-last-name" placeholder={t("Nachname", "Last name")} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className={inputCls} />
            </div>
            <input data-testid="tc-email" type="email" placeholder={t("E-Mail (für deine Mitgliedschaft)", "Email (for your membership)")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
            <p className="text-neutral-500 text-xs">{t("Wichtig: Diese E-Mail nutzt du später beim Buchen deiner Kurse, damit dein Abo erkannt wird.", "Important: use this email when booking your courses so your membership is recognized.")}</p>
          </div>

          <div className="mt-6 space-y-4" data-testid="tc-consent-section">
            <button type="button" disabled={!terms} onClick={() => setShowTerms(true)} data-testid="tc-read-terms" className="inline-flex items-center gap-2 border border-[#ff0033]/60 text-[#ff0033] px-4 py-2.5 hover:bg-[#ff0033]/10 transition-colors disabled:opacity-40"><BookOpen size={16} />AGB lesen</button>
            {termsError && <p role="alert" data-testid="tc-terms-error" className="text-red-400 text-sm">AGB konnten nicht geladen werden. <button type="button" onClick={retry} data-testid="tc-terms-retry" className="underline">Erneut laden</button></p>}
            <label className="flex items-start gap-3 text-sm text-neutral-200 cursor-pointer" data-testid="tc-terms-label">
              <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} disabled={!terms || !!loading} data-testid="tc-terms-accept" className="mt-0.5 h-4 w-4 shrink-0 accent-[#ff0033]" />
              <span>Ich habe die AGB zum Trainingsclub gelesen.</span>
            </label>
          </div>

          <div className="mt-6 space-y-3">
            <button onClick={() => go("stripe")} disabled={!valid || loading} data-testid="tc-pay-stripe" className="w-full bg-[#ff0033] text-white font-heading text-lg uppercase tracking-wider py-4 hover:bg-[#cc0029] transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
              {loading === "stripe" ? <Loader2 className="animate-spin" size={18} /> : <><CreditCard size={18} /> {t("Mit Karte / Stripe abonnieren", "Subscribe with card / Stripe")}</>}
            </button>
            {info?.paypal_enabled && (
              <button onClick={() => go("paypal")} disabled={!valid || loading} data-testid="tc-pay-paypal" className="w-full border border-white/20 text-white font-mono-lab text-xs uppercase tracking-widest py-3.5 hover:border-[#ffc439] hover:text-[#ffc439] transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
                {loading === "paypal" ? <Loader2 className="animate-spin" size={16} /> : t("Mit PayPal abonnieren", "Subscribe with PayPal")}
              </button>
            )}
          </div>
          <p data-testid="tc-renewal-notice" className="text-neutral-400 text-xs text-center mt-4">{t("Automatische Verlängerung alle 28 Tage. Kündigung spätestens 14 Tage vor dem nächsten Abrechnungstermin.", "Automatic renewal every 28 days. Cancel at least 14 days before the next billing date.")}</p>
        </div>
      </div>
      <TrainingsclubTermsDialog open={showTerms} onOpenChange={setShowTerms} terms={terms} />
    </div>
  );
};

export default TrainingsclubModal;
